import type { QueryClient } from "react-query";

export type SocialCustomEntry = {
  id: string;
  name?: string;
  link?: string;
  iconUrl?: string;
  originalIconUrl?: string | null;
  cropState?: unknown;
  cropViewport?: unknown;
};

/** `user.social_custom` is stored as text or jsonb — accept both. */
export function parseSocialCustomList(raw: unknown): SocialCustomEntry[] {
  try {
    const parsed =
      typeof raw === "string" ? (raw.trim() ? JSON.parse(raw) : null) : raw;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((entry: any) => ({
        id: String(entry?.id || "").trim(),
        name: String(entry?.name || "").trim() || undefined,
        link: String(entry?.link || "").trim() || undefined,
        iconUrl: String(entry?.iconUrl || "").trim() || undefined,
        originalIconUrl: entry?.originalIconUrl ?? null,
        cropState: entry?.cropState ?? null,
        cropViewport: entry?.cropViewport ?? null,
      }))
      .filter((entry) => entry.id);
  } catch {
    return [];
  }
}

/** True when `key` refers to an existing custom social entry (not the "add custom" picker). */
export function isCustomSocialEditKey(
  key: unknown,
  existing: SocialCustomEntry[] = [],
): boolean {
  const id = String(key || "").trim();
  if (!id || id === "custom") return false;
  if (id.startsWith("custom-")) return true;
  return existing.some((entry) => entry.id === id);
}

/** Prefer live overlay, then prop, then query row — so edit sees icons just added. */
export function resolveSocialCustomList(args: {
  userId?: string | null;
  username?: string | null;
  prop?: unknown;
  query?: unknown;
}): SocialCustomEntry[] {
  const live = getLiveSocialCustom({
    userId: args.userId,
    username: args.username,
  });
  if (live) return parseSocialCustomList(live.socialCustom);
  const fromProp = parseSocialCustomList(args.prop);
  if (fromProp.length) return fromProp;
  return parseSocialCustomList(args.query);
}

export type SocialLinkRow = { platform: string; url: string };

/** Stale template seed: social_custom JSON was once mapped to a generic "Link". */
export function isBogusSocialCustomLink(entry: {
  platform?: string;
  url?: string;
}): boolean {
  const platform = String(entry.platform || "")
    .trim()
    .toLowerCase();
  if (platform !== "link" && platform !== "custom") return false;
  const url = String(entry.url || "").trim();
  return url.startsWith("[") || url.startsWith("{");
}

export function isGenericSocialLinkLabel(platform: string | undefined): boolean {
  const p = String(platform || "")
    .trim()
    .toLowerCase();
  return p === "link" || p === "custom";
}

/**
 * Build the ordered social list used by Hero chips, footer, and the inspector.
 * Known platforms come from `social_*` columns; custom rows use `social_custom` names.
 */
export function buildOrderedSocialLinks(args: {
  known?: SocialLinkRow[];
  customRaw?: unknown;
  orderRaw?: string | null;
}): SocialLinkRow[] {
  const labelToOrderKey: Record<string, string> = {
    instagram: "instagram",
    youtube: "youtube",
    tiktok: "tiktok",
    linkedin: "linkedin",
    ltk: "ltk",
    amazon: "amazon",
    spotify: "spotify",
    twitter: "twitter",
    x: "twitter",
    facebook: "facebook",
    pinterest: "pinterest",
    shopmy: "shopmy",
    shop: "shop",
    snapchat: "snapchat",
    podcast: "podcast",
    applepodcasts: "applepodcast",
    "apple podcasts": "applepodcast",
    newsletter: "newsletter",
    discord: "discord",
    website: "website",
  };

  const byKey = new Map<string, SocialLinkRow>();

  for (const row of args.known || []) {
    const url = String(row.url || "").trim();
    if (!url || isBogusSocialCustomLink(row)) continue;
    if (isGenericSocialLinkLabel(row.platform)) continue;
    const label = String(row.platform || "").trim();
    if (!label) continue;
    const normalized = label.toLowerCase().replace(/\s+/g, "");
    const orderKey =
      labelToOrderKey[label.toLowerCase()] ||
      labelToOrderKey[normalized] ||
      normalized.replace(/^social_/, "");
    const entry = { platform: label, url };
    byKey.set(orderKey, entry);
    byKey.set(normalized, entry);
  }

  const customs = parseSocialCustomList(args.customRaw);
  const customUrls = new Set(
    customs
      .map((entry) => String(entry.link || "").trim().toLowerCase())
      .filter(Boolean),
  );
  // Drop known rows that are actually custom URLs (stale "mnbn" keyed wrong, etc.).
  if (customUrls.size) {
    for (const [key, row] of Array.from(byKey.entries())) {
      if (customUrls.has(String(row.url || "").trim().toLowerCase())) {
        byKey.delete(key);
      }
    }
  }

  for (const entry of customs) {
    const url = String(entry.link || "").trim();
    if (!url) continue;
    const id = String(entry.id || "").trim() || `custom-${url}`;
    const label = String(entry.name || "").trim() || "Link";
    byKey.set(id.toLowerCase(), { platform: label, url });
  }

  const order = String(args.orderRaw || "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);

  if (!order.length) {
    const seen = new Set<string>();
    const out: SocialLinkRow[] = [];
    byKey.forEach((row) => {
      const dedupe = `${row.platform}::${row.url}`.toLowerCase();
      if (seen.has(dedupe)) return;
      seen.add(dedupe);
      out.push(row);
    });
    return out;
  }

  const out: SocialLinkRow[] = [];
  const used = new Set<string>();
  for (const raw of order) {
    const hit = byKey.get(raw.toLowerCase());
    if (!hit) continue;
    const dedupe = `${hit.platform}::${hit.url}`.toLowerCase();
    if (used.has(dedupe)) continue;
    used.add(dedupe);
    out.push(hit);
  }
  byKey.forEach((hit) => {
    const dedupe = `${hit.platform}::${hit.url}`.toLowerCase();
    if (used.has(dedupe)) return;
    used.add(dedupe);
    out.push(hit);
  });
  return out;
}

/** Display label for a known `social_*` order key (e.g. instagram → Instagram). */
const KNOWN_ORDER_KEY_LABEL: Record<string, string> = {
  instagram: "Instagram",
  youtube: "YouTube",
  tiktok: "TikTok",
  linkedin: "LinkedIn",
  ltk: "LTK",
  amazon: "Amazon",
  spotify: "Spotify",
  twitter: "Twitter",
  x: "Twitter",
  facebook: "Facebook",
  pinterest: "Pinterest",
  shopmy: "ShopMy",
  shop: "Shop",
  snapchat: "Snapchat",
  podcast: "Podcast",
  applepodcast: "Apple Podcasts",
  newsletter: "Newsletter",
  discord: "Discord",
  website: "Website",
};

function knownRowOrderKey(row: SocialLinkRow): string {
  const label = String(row.platform || "").trim();
  const normalized = label.toLowerCase().replace(/\s+/g, "");
  const labelToOrderKey: Record<string, string> = {
    instagram: "instagram",
    youtube: "youtube",
    tiktok: "tiktok",
    linkedin: "linkedin",
    ltk: "ltk",
    amazon: "amazon",
    spotify: "spotify",
    twitter: "twitter",
    x: "twitter",
    facebook: "facebook",
    pinterest: "pinterest",
    shopmy: "shopmy",
    shop: "shop",
    snapchat: "snapchat",
    podcast: "podcast",
    applepodcasts: "applepodcast",
    "apple podcasts": "applepodcast",
    newsletter: "newsletter",
    discord: "discord",
    website: "website",
  };
  return (
    labelToOrderKey[label.toLowerCase()] ||
    labelToOrderKey[normalized] ||
    normalized.replace(/^social_/, "")
  );
}

/**
 * Apply deferred known-platform url overrides (empty string = removed).
 * Keys are order ids (`instagram`) or column names (`social_instagram`).
 */
export function applyKnownSocialOverrides(
  known: SocialLinkRow[],
  overrides?: Record<string, string> | null,
): SocialLinkRow[] {
  if (!overrides || !Object.keys(overrides).length) return known;
  const byKey = new Map<string, SocialLinkRow>();
  for (const row of known) {
    const key = knownRowOrderKey(row);
    if (key) byKey.set(key, row);
  }
  for (const [rawKey, rawUrl] of Object.entries(overrides)) {
    const key = String(rawKey || "")
      .trim()
      .toLowerCase()
      .replace(/^social_/, "");
    if (!key) continue;
    const url = String(rawUrl || "").trim();
    if (!url) {
      byKey.delete(key);
      continue;
    }
    const label =
      KNOWN_ORDER_KEY_LABEL[key] ||
      key.charAt(0).toUpperCase() + key.slice(1);
    byKey.set(key, { platform: label, url });
  }
  return Array.from(byKey.values());
}

/**
 * Re-label / rebuild a social list so generic "Link" chips pick up custom names.
 * Prefer live overlay, then explicit customRaw, then keep known platforms from `social`.
 */
export function resolveSocialLinksForDisplay(args: {
  social?: SocialLinkRow[] | null;
  userId?: string | null;
  username?: string | null;
  customRaw?: unknown;
  orderRaw?: string | null;
}): SocialLinkRow[] {
  const live = getLiveSocialCustom({
    userId: args.userId,
    username: args.username,
  });
  const customRaw =
    live?.socialCustom ??
    (args.customRaw !== undefined ? args.customRaw : undefined);
  const customs = parseSocialCustomList(customRaw);
  const orderRaw =
    live?.socialLinksOrder != null
      ? live.socialLinksOrder
      : args.orderRaw;

  const base = (Array.isArray(args.social) ? args.social : []).filter(
    (row) => !isBogusSocialCustomLink(row),
  );
  const known = applyKnownSocialOverrides(
    base.filter((row) => !isGenericSocialLinkLabel(row.platform)),
    live?.knownOverrides,
  );

  // Keep the incoming order (already `social_links_order`) unless we have a
  // fresher order string to apply.
  if (!String(orderRaw || "").trim()) {
    if (!customs.length) return known;
    const seen = new Set(
      known.map((row) => String(row.url || "").trim().toLowerCase()),
    );
    const extra = customs
      .map((entry) => {
        const url = String(entry.link || "").trim();
        if (!url || seen.has(url.toLowerCase())) return null;
        return {
          platform: String(entry.name || "").trim() || "Link",
          url,
        };
      })
      .filter(Boolean) as SocialLinkRow[];
    return [...known, ...extra];
  }

  return buildOrderedSocialLinks({
    known,
    customRaw,
    orderRaw,
  });
}

type LiveSocialCustom = {
  userId: string;
  username?: string;
  socialCustom: string;
  socialLinksOrder?: string | null;
  /** Known platform order-key → url; empty string removes from live preview. */
  knownOverrides?: Record<string, string>;
};

let liveSocialCustom: LiveSocialCustom | null = null;
const liveSocialCustomListeners = new Set<() => void>();

/** Dashboard preview reads this immediately — profile queries can lag a refetch. */
export function publishLiveSocialCustom(next: LiveSocialCustom) {
  liveSocialCustom = next;
  liveSocialCustomListeners.forEach((listener) => listener());
}

/** Drop optimistic social overlay so Discard restores DB order / customs. */
export function clearLiveSocialCustom() {
  if (!liveSocialCustom) return;
  liveSocialCustom = null;
  liveSocialCustomListeners.forEach((listener) => listener());
}

export function getLiveSocialCustom(opts?: {
  userId?: string | null;
  username?: string | null;
}): LiveSocialCustom | null {
  if (!liveSocialCustom) return null;
  const uid = String(opts?.userId || "").trim();
  const uname = String(opts?.username || "").trim().toLowerCase();
  const liveUid = String(liveSocialCustom.userId || "").trim();
  const liveName = String(liveSocialCustom.username || "").trim().toLowerCase();
  if (!uid && !uname) return liveSocialCustom;
  if (uid && liveUid && uid === liveUid) return liveSocialCustom;
  if (uname && liveName && uname === liveName) return liveSocialCustom;
  if (!liveUid && !liveName) return liveSocialCustom;
  return null;
}

export function subscribeLiveSocialCustom(listener: () => void) {
  liveSocialCustomListeners.add(listener);
  return () => {
    liveSocialCustomListeners.delete(listener);
  };
}

/**
 * Write the new custom-link list into the dashboard social-links query and
 * every loaded profile query so the editor and public preview update at once.
 */
export function patchSocialCustomCaches(
  queryClient: QueryClient,
  args: {
    userId?: string | null;
    username?: string | null;
    socialCustom: string;
    socialLinksOrder?: string | null;
  },
) {
  const uid = String(args.userId || "").trim();
  const uname = String(args.username || "").trim().toLowerCase();
  const { socialCustom, socialLinksOrder } = args;

  if (uid) {
    queryClient.setQueryData(["getSocialLinks", uid], (prev: any) => {
      if (!prev?.data) return prev;
      return {
        ...prev,
        data: {
          ...prev.data,
          social_custom: socialCustom,
          ...(socialLinksOrder != null
            ? { social_links_order: socialLinksOrder }
            : {}),
        },
        active: {
          ...prev.active,
          custom: socialCustom,
        },
      };
    });
  }

  queryClient.getQueryCache().findAll(["fetchProfileSetup"]).forEach((query) => {
    const keyName = String((query.queryKey as unknown[])?.[1] || "")
      .trim()
      .toLowerCase();
    queryClient.setQueryData(query.queryKey, (prev: any) => {
      const userData = prev?.combinedRes?.userData;
      if (!userData) return prev;
      const rowId = String(userData.user_id || "").trim();
      const rowName = String(userData.username || "").trim().toLowerCase();
      const matches =
        (uid && rowId === uid) ||
        (uname && (rowName === uname || keyName === uname)) ||
        (!rowId && !rowName && !keyName);
      if (!matches) return prev;
      return {
        ...prev,
        combinedRes: {
          ...prev.combinedRes,
          userData: {
            ...userData,
            social_custom: socialCustom,
            ...(socialLinksOrder != null
              ? { social_links_order: socialLinksOrder }
              : {}),
          },
        },
      };
    });
  });

  if (uid) {
    void queryClient.invalidateQueries(["getSocialLinks", uid]);
  } else {
    void queryClient.invalidateQueries(["getSocialLinks"]);
  }
  void queryClient.invalidateQueries(["fetchProfileSetup"]);
  const prevLive = getLiveSocialCustom({ userId: uid, username: uname });
  publishLiveSocialCustom({
    userId: uid,
    username: uname,
    socialCustom,
    socialLinksOrder,
    knownOverrides: prevLive?.knownOverrides,
  });
}
